import React, { useState, useEffect } from 'react'
import { Package, Plus, Pencil, Trash2, Search } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import StatusBadge from '../../components/StatusBadge'
import ProductForm from '../../components/admin/ProductForm'
import G2BulkCatalogPicker from '../../components/admin/G2BulkCatalogPicker'
import DynamicIcon from '../../components/DynamicIcon'

const Products = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editProduct, setEditProduct] = useState(null)
  const [showCatalog, setShowCatalog] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const api = useApi()

  const fetchProducts = async () => {
    try {
      const data = await api.getAdminProducts()
      setProducts(data.products || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchProducts() }, [])

  // Helper function to get Lucide icon component from name string
  const getIcon = (iconName) => {
    return <DynamicIcon name={iconName} size={18} className="text-gray-600" />
  }

  // Filter and rank products by search relevance
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm ||
                         product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && product.is_active) ||
                         (statusFilter === 'inactive' && !product.is_active)

    return matchesSearch && matchesCategory && matchesStatus
  }).sort((a, b) => {
    if (!searchTerm) return 0
    const term = searchTerm.toLowerCase()
    const aName = a.name.toLowerCase()
    const bName = b.name.toLowerCase()
    const aExact = aName === term
    const bExact = bName === term
    if (aExact !== bExact) return aExact ? -1 : 1
    const aStarts = aName.startsWith(term)
    const bStarts = bName.startsWith(term)
    if (aStarts !== bStarts) return aStarts ? -1 : 1
    const aNameMatch = aName.includes(term)
    const bNameMatch = bName.includes(term)
    if (aNameMatch !== bNameMatch) return aNameMatch ? -1 : 1
    return 0
  })

  const categories = [...new Set(products.map(p => p.category))]

  const handleCreate = () => {
    setEditProduct(null)
    setShowForm(true)
  }

  const handleEdit = (product) => {
    setEditProduct(product)
    setShowForm(true)
  }

  const handleDelete = async (product) => {
    if (!confirm(`Delete "${product.name}"? This will deactivate the product.`)) return
    try {
      await api.deleteProduct(product.id)
      await fetchProducts()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleSubmit = async (formData) => {
    setSaving(true)
    try {
      if (editProduct) {
        await api.updateProduct(editProduct.id, formData)
      } else {
        await api.createProduct(formData)
      }
      setShowForm(false)
      setEditProduct(null)
      await fetchProducts()
    } catch (err) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCatalogSelect = (item) => {
    setEditProduct(prev => ({
      ...prev,
      g2bulk_catalog_id: item.id?.toString() || '',
      name: item.name || prev?.name || '',
    }))
    setShowCatalog(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="spinner w-10 h-10"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Package size={24} className="text-accent" />
          <h1 className="text-2xl font-bold text-text-primary">Products</h1>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={16} className="mr-2" />
          Add Product
        </button>
      </div>

      {error && <div className="text-error text-sm">{error}</div>}

      {/* Search and Filters */}
      {!showForm && (
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="input"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-text-muted">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary">
              {editProduct ? 'Edit Product' : 'New Product'}
            </h2>
            <button
              onClick={() => setShowCatalog(true)}
              className="btn btn-secondary text-xs"
            >
              Browse Catalog
            </button>
          </div>
          <ProductForm
            product={editProduct}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setEditProduct(null) }}
            loading={saving}
          />
        </div>
      )}

      {showCatalog && (
        <G2BulkCatalogPicker
          onSelect={handleCatalogSelect}
          onClose={() => setShowCatalog(false)}
        />
      )}

      {/* Products table */}
      {!showForm && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Denomination</th>
                  <th>Catalog ID</th>
                  <th>Codes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-text-muted">
                      {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ? 
                        'No products match your filters.' : 
                        'No products yet. Create one to get started.'}
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="flex items-center space-x-3">
                          {getIcon(p.icon)}
                          <span className="text-text-primary font-medium text-sm">{p.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="capitalize text-text-secondary text-sm">{p.category}</span>
                      </td>
                      <td className="text-accent text-sm font-medium">{p.denomination}</td>
                      <td className="text-text-muted text-xs font-mono">{p.g2bulk_catalog_id || '—'}</td>
                      <td>
                        <div className="text-sm">
                          <span className="text-success font-medium">{p.active_code_count || 0}</span>
                          <span className="text-text-muted"> / {p.total_code_count || 0}</span>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={p.is_active ? 'active' : 'expired'} size="xs" />
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(p)}
                            className="text-accent hover:text-accent-light transition-colors p-1"
                            title="Edit product"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            className="text-error hover:text-error/80 transition-colors p-1"
                            title="Delete product"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Products